import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect, useState} from "react";
import type {ImageElement} from "slate";
import {useSlate, type RenderElementProps} from "slate-react";

import {addStyle} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Popover, PopoverContentCore} from "@khanacademy/wonder-blocks-popover";
import {spacing} from "@khanacademy/wonder-blocks-tokens";

import {deleteImageNode, updateImageNode} from "../helpers/image-helpers.ts";
import {isSelectionWithinNode} from "../helpers/node-helpers.ts";

import trashIcon from "@phosphor-icons/core/regular/trash.svg";

const StyledSpan = addStyle("span");
const StyledImg = addStyle("img");

export default function ImageNode(
    props: RenderElementProps,
): React.ReactElement {
    const {element, attributes, children} = props;

    const image = element as ImageElement;
    const editor = useSlate();
    const [url, setUrl] = useState(image.url || "");
    const isSelectionWithin = isSelectionWithinNode(editor, element);

    useEffect(() => {
        updateImageNode(editor, url);
    }, [editor, url]);

    return (
        <StyledSpan {...attributes} style={styles.container}>
            {children}
            <Popover
                dismissEnabled
                opened={isSelectionWithin}
                content={
                    <PopoverContentCore style={styles.popover}>
                        <TextField
                            id="coeditor-image-url-editor"
                            value={url}
                            onChange={setUrl}
                            style={styles.url}
                            placeholder={t`Image URL`}
                            autoFocus
                            autoComplete="off"
                        />
                        <IconButton
                            icon={trashIcon}
                            actionType="destructive"
                            kind="tertiary"
                            style={styles.deleteButton}
                            onClick={() => deleteImageNode(editor, image.url)}
                        />
                    </PopoverContentCore>
                }
            >
                <StyledImg
                    {...attributes}
                    src={image.url}
                    alt={image.alt || t`Image`}
                    style={styles.image}
                />
            </Popover>
        </StyledSpan>
    );
}

const styles = StyleSheet.create({
    container: {
        marginBlockEnd: spacing.medium_16,
    },
    image: {
        display: "inline-block",
        maxWidth: "100%",
        cursor: "pointer",
    },
    popover: {
        display: "flex",
        flexDirection: "row",
        padding: spacing.xSmall_8,
    },
    url: {
        paddingInlineStart: spacing.xSmall_8,
    },
    deleteButton: {
        flex: 0,
        width: spacing.xLarge_32,
        padding: spacing.xxxSmall_4,
        marginInlineStart: spacing.xxxSmall_4,
        border: 0,
    },
});
